/**
 * Lightweight markdown parser for chat messages.
 *
 * Supports: bold, italic, links, phone numbers, unordered lists,
 * ordered lists, and line breaks. Uses simple regex transforms
 * instead of a full markdown library to keep the bundle small.
 *
 * SECURITY: text is escaped before processing, but this function is NOT a
 * sanitizer and MUST NOT be treated as one. Its output is assembled into
 * attribute values (see the link and phone transforms below), so it is only
 * safe when the caller passes the result through DOMPurify — which both live
 * sinks do (`TextBubble.ts`, `MessageList.ts`). This module is exported from
 * the widget's public surface (`src/index.ts`); a caller that renders its
 * output directly is responsible for its own sanitization.
 *
 * The previous version of this note claimed "no raw user input can produce
 * executable markup", which was false: quotes were not escaped, so a crafted
 * URL could inject attributes. Security review 2026-08-12.
 */

/**
 * Escape HTML entities.
 *
 * `textContent` -> `innerHTML` escapes `&`, `<` and `>` but NOT quotes, and
 * every value here ends up inside a double-quoted attribute. Without the
 * explicit quote pass, `[c](https://x" rel="opener)` closed `href` early and
 * injected its own `rel` — and HTML's duplicate-attribute rule takes the
 * FIRST, so `rel="opener"` won and the `rel="noopener noreferrer"` below was
 * dead. That re-enables `window.opener` on an attacker-influenced link
 * (reverse tabnabbing); bot text is LLM output and Phase 1 `bot_response`,
 * both untrusted. DOMPurify strips `on*` handlers so this was never XSS, but
 * `rel` is in ALLOWED_ATTR and so passed straight through.
 */
function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML.replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

/**
 * Parse a simple markdown string into safe HTML.
 *
 * Processing order matters: escape HTML first, then apply
 * markdown transforms on the escaped output. Links and phone
 * numbers are detected and wrapped in anchor tags.
 *
 * @param text - Raw markdown text from the backend
 * @returns Safe HTML string
 */
export function parseMarkdown(text: string): string {
  // Step 1: Escape all HTML entities
  let html = escapeHtml(text);

  // Step 2: Process block-level elements (lists)
  html = processLists(html);

  // Step 3: Bold (**text** or __text__)
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/__(.+?)__/g, "<strong>$1</strong>");

  // Step 4: Italic (*text* or _text_) — must run after bold
  html = html.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, "<em>$1</em>");
  html = html.replace(/(?<!_)_(?!_)(.+?)(?<!_)_(?!_)/g, "<em>$1</em>");

  // Step 5: Links [text](url) — restrict to http/https to prevent javascript: XSS.
  // The URL class excludes quotes and whitespace as well as ')': the value is
  // interpolated into a double-quoted attribute, so a quote inside it would
  // close `href` early and inject sibling attributes. Defence-in-depth with
  // the quote-escaping in escapeHtml — either alone closes it.
  html = html.replace(
    /\[([^\]]+)\]\((https?:\/\/[^)"'\s]+)\)/g,
    '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
  );

  // Step 6: Auto-link URLs not already inside an anchor. Same quote exclusion
  // as Step 5 — redundant while escapeHtml escapes quotes, kept so that
  // removing that pass cannot silently re-open attribute injection here.
  html = html.replace(
    /(?<!href="|">)(https?:\/\/[^\s<"']+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>',
  );

  // Step 7: Phone numbers (Singapore format: +65 XXXX XXXX, 8-digit, etc.)
  html = html.replace(
    /(?<!["\w])(\+?65[\s-]?\d{4}[\s-]?\d{4})(?!["\w])/g,
    '<a href="tel:$1" class="hana-msg__phone">$1</a>',
  );
  html = html.replace(
    /(?<!["\w\/])(\b\d{4}[\s-]\d{4}\b)(?!["\w])/g,
    '<a href="tel:$1" class="hana-msg__phone">$1</a>',
  );

  // Step 8: Line breaks (double newline = paragraph break, single = <br>)
  html = html.replace(/\n\n/g, "</p><p>");
  html = html.replace(/\n/g, "<br>");

  // Wrap in paragraph if we created paragraph breaks
  if (html.includes("</p><p>")) {
    html = "<p>" + html + "</p>";
  }

  return html;
}

/**
 * Process unordered and ordered list blocks.
 * Detects consecutive lines starting with - or 1. patterns.
 */
function processLists(html: string): string {
  // Unordered lists: lines starting with "- "
  html = html.replace(
    /(?:^|\n)((?:- .+(?:\n|$))+)/g,
    (_match: string, block: string) => {
      const items = block
        .split("\n")
        .filter((line) => line.startsWith("- "))
        .map((line) => `<li>${line.slice(2)}</li>`)
        .join("");
      return `\n<ul class="hana-msg__list">${items}</ul>\n`;
    },
  );

  // Ordered lists: lines starting with "1. ", "2. ", etc.
  html = html.replace(
    /(?:^|\n)((?:\d+\. .+(?:\n|$))+)/g,
    (_match: string, block: string) => {
      const items = block
        .split("\n")
        .filter((line) => /^\d+\. /.test(line))
        .map((line) => `<li>${line.replace(/^\d+\. /, "")}</li>`)
        .join("");
      return `\n<ol class="hana-msg__list">${items}</ol>\n`;
    },
  );

  return html;
}
